import { useCallback, useEffect, useState } from 'react';
import { normalizeEndpoint } from '@/lib/normalize-endpoint';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseSearchResultsParams {
  pageSize?: number;
}

export const useSearchResults = <T = any>(
  searchEndpoint?: string,
  otherSearchEndpoint?: string,
  params?: UseSearchResultsParams
) => {
  const [searchResults, setSearchResults] = useState<T[]>([]);
  const [selectedSearchResult, setSelectedSearchResult] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const { entity } = useAuth();
  const { pageSize = 15 } = params || {};

  const fetchSearchResults = useCallback(
    async (
      endpoint: string | undefined,
      otherEndpoint: string | undefined,
      query = '',
      paginationParams?: { page?: number; pageSize?: number }
    ) => {
      if (!endpoint && !otherEndpoint) return [];

      setIsLoading(true);

      try {
        const url = otherEndpoint ? otherEndpoint : `entities/${entity?.slug}/erp/${normalizeEndpoint(endpoint)}`;

        const requestParams: any = {};

        if (query) {
          requestParams.search = query;
        }

        // Add pagination parameters
        if (paginationParams?.page !== undefined) {
          requestParams.page = paginationParams.page + 1;
        }
        if (paginationParams?.pageSize !== undefined) {
          requestParams.page_size = paginationParams.pageSize;
        }

        const response = await api.get(url, { params: requestParams });

        if (response.status !== 200) {
          throw new Error(`API request failed with status ${response.status}`);
        }

        const results = response.data.results || ([] as T[]);
        setSearchResults(results);
        setTotalItems(response.data.count || results.length);
        setSelectedSearchResult(null);
        return results;
      } catch (error) {
        setSearchResults([]);
        setTotalItems(0);
        return [] as T[];
      } finally {
        setIsLoading(false);
      }
    },
    [entity?.slug]
  );

  // Handle page change for pagination
  const handlePageChange = useCallback(
    async (page: number, searchQuery = '') => {
      setCurrentPage(page);
      await fetchSearchResults(searchEndpoint, otherSearchEndpoint, searchQuery, { page, pageSize });
    },
    [fetchSearchResults, searchEndpoint, otherSearchEndpoint, pageSize]
  );

  useEffect(() => {
    if (otherSearchEndpoint || searchEndpoint) {
      fetchSearchResults(searchEndpoint, otherSearchEndpoint, '', { page: currentPage, pageSize });
    }
  }, [otherSearchEndpoint, searchEndpoint, fetchSearchResults, currentPage, pageSize]);

  return {
    searchResults,
    setSearchResults,
    selectedSearchResult,
    setSelectedSearchResult,
    fetchSearchResults,
    isLoading,
    totalItems,
    currentPage,
    handlePageChange
  } as const;
};
